import { FeatureFlag } from '@mews-commander/enums';
import { useDialogForm } from '@mews-commander/form';
import { DividerList, LabeledListItem, TooltippedSpan } from '@mews-commander/ui';
import { UnicodeEntity } from '@mews/enums';
import { lt, useFeatureFlag } from '@mews/framework';
import { IconButton, IconName, Stack, Tag, Typography } from '@optimus-web/core';
import { EditProductAccountingForm } from './forms/EditProductAccountingForm';
import type { LocalProductSidePanelProps } from './types';

export const LocalProductAccounting = (props: LocalProductSidePanelProps<'accounting'>) => {
    const {
        accountingCategory,
        rebateAccountingCategory,
        cancelledItemAccountingCategory,
        propertyId,
        enterpriseProductId,
        centralProductId,
    } = props;

    const centralProductsEditEnabled = useFeatureFlag(FeatureFlag.centralProductsEditEnabled);

    const [renderEditProductAccountingForm, toggleEditProductAccountingForm] = useDialogForm({
        title: lt('EditAccounting'),
        cancelLabel: lt('GoBack'),
        renderForm: (formProps) => (
            <EditProductAccountingForm
                {...formProps}
                accountingCategoryKey={accountingCategory ?? undefined}
                cancellationAccountingCategoryKey={cancelledItemAccountingCategory ?? undefined}
                centralProductId={centralProductId as string}
                enterpriseProductId={enterpriseProductId}
                formRef={formProps.formRef}
                propertyId={propertyId}
                rebateAccountingCategoryKey={rebateAccountingCategory ?? undefined}
            />
        ),
    });

    const listItems = [
        {
            id: 'accountingCategory',
            label: lt('ConfigureProductAccountingCategoryDropdown'),
            value: accountingCategory,
        },
        {
            id: 'rebateAccountingCategory',
            label: lt('ConfigureProductRebateDropdown'),
            value: rebateAccountingCategory,
        },
        {
            id: 'cancelledItemAccountingCategory',
            label: lt('ConfigureProductCanceledDropdown'),
            value: cancelledItemAccountingCategory,
        },
    ];

    return (
        <>
            {centralProductsEditEnabled && (
                <Stack distribution="space-between">
                    <Typography as="div" textStyle="titleSmallStrong">
                        {lt('AccountingDetail')}
                    </Typography>
                    <TooltippedSpan tooltip={lt('EditAccountingProductTooltip')}>
                        <IconButton
                            icon={IconName.Edit}
                            type="button"
                            size="small"
                            onClick={toggleEditProductAccountingForm}
                        />
                    </TooltippedSpan>
                </Stack>
            )}
            <DividerList>
                {listItems.map(({ id, label, value }) => (
                    <LabeledListItem key={id} data-test-id={id} label={label}>
                        {value ? (
                            <Tag variant="basicSubtle" outline>
                                {value.name}
                            </Tag>
                        ) : (
                            UnicodeEntity.Dash
                        )}
                    </LabeledListItem>
                ))}
            </DividerList>
            {centralProductId && renderEditProductAccountingForm()}
        </>
    );
};
